import { CatalogPager as LinearPager, Currency } from '../components/ui/DataPrimitives';
import { formatNumber as formatCount } from '../utils/numberFormat';
import { PageShell, TableShell } from '../components/ui/DataPrimitives';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../config';
import './ListLinear.css';

function MaterialList() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [purchasedCount, setPurchasedCount] = useState(0);

  // Taxonomy cascading filters
  const [level1Filter, setLevel1Filter] = useState('');
  const [level2Filter, setLevel2Filter] = useState('');
  const [level3Filter, setLevel3Filter] = useState('');
  const [level4Filter, setLevel4Filter] = useState('');
  const [level1Options, setLevel1Options] = useState([]);
  const [level2Options, setLevel2Options] = useState([]);
  const [level3Options, setLevel3Options] = useState([]);
  const [level4Options, setLevel4Options] = useState([]);

  // Sorting (client-side within current page)
  const [sortColumn, setSortColumn] = useState('annual_spend');
  const [sortDirection, setSortDirection] = useState('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const materialsTableRef = useRef(null);

  useEffect(() => {
    fetchTaxonomy();
  }, [token]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // Fetch taxonomy subcategories with counts for a given level
  const fetchSubcategories = async (level, params = {}) => {
    try {
      const qs = new URLSearchParams({ level, ...params }).toString();
      const response = await fetch(`${API_BASE}/api/taxonomy/subcategories?${qs}`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        return data.values || [];
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
    return [];
  };

  // Cascade: when L1 changes, fetch L2 options and clear L2-L4
  useEffect(() => {
    if (level1Filter) {
      fetchSubcategories(2, { level_1: level1Filter }).then(setLevel2Options);
    } else {
      setLevel2Options([]);
    }
    setLevel2Filter('');
    setLevel3Filter('');
    setLevel4Filter('');
    setLevel3Options([]);
    setLevel4Options([]);
  }, [level1Filter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cascade: when L2 changes, fetch L3 options and clear L3-L4
  useEffect(() => {
    if (level1Filter && level2Filter) {
      fetchSubcategories(3, { level_1: level1Filter, level_2: level2Filter }).then(setLevel3Options);
    } else {
      setLevel3Options([]);
    }
    setLevel3Filter('');
    setLevel4Filter('');
    setLevel4Options([]);
  }, [level2Filter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cascade: when L3 changes, fetch L4 options and clear L4
  useEffect(() => {
    if (level1Filter && level2Filter && level3Filter) {
      fetchSubcategories(4, { level_1: level1Filter, level_2: level2Filter, level_3: level3Filter }).then(setLevel4Options);
    } else {
      setLevel4Options([]);
    }
    setLevel4Filter('');
  }, [level3Filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('per_page', pageSize);
      if (searchQuery) params.append('search', searchQuery);
      if (level1Filter) params.append('level_1', level1Filter);
      if (level2Filter) params.append('level_2', level2Filter);
      if (level3Filter) params.append('level_3', level3Filter);
      if (level4Filter) params.append('level_4', level4Filter);

      const response = await fetch(`${API_BASE}/api/materials?${params}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setMaterials(data.materials || []);
        setTotalCount(data.total_count || 0);
        setPurchasedCount(data.purchased_last_12mo_count || 0);
      } else {
        setError('Unable to load materials. Please refresh the page.');
      }
    } catch (err) {
      console.error('Error fetching materials:', err);
      setError('Unable to load materials. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when page, filters, or search change (debounced for search)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMaterials();
    }, searchQuery ? 300 : 0);
    return () => clearTimeout(timer);
  }, [currentPage, pageSize, searchQuery, level1Filter, level2Filter, level3Filter, level4Filter, token]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, level1Filter, level2Filter, level3Filter, level4Filter]);

  const fetchTaxonomy = async () => {
    const options = await fetchSubcategories(1);
    setLevel1Options(options);
  };

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const hasActiveFilters = searchQuery || level1Filter || level2Filter || level3Filter || level4Filter;

  const clearFilters = () => {
    setSearchQuery('');
    setLevel1Filter('');
    setLevel2Filter('');
    setLevel3Filter('');
    setLevel4Filter('');
  };

  // Sort materials client-side within the current page
  const displayMaterials = useMemo(() => {
    return [...materials].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Handle nulls — push them to the end
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Numeric columns
      if (['supplier_count', 'annual_spend', 'risk_score'].includes(sortColumn)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      // Numeric-ish columns (contract count)
      else if (sortColumn === 'contract_count') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      }
      // String columns
      else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [materials, sortColumn, sortDirection]);

  // Annual spend in $M to 2 decimals per Rev-1 (e.g. $2.26M); "—" when no 12-mo spend.
  const categoryOf = (m) => m.category_label || m.level_2 || m.level_1 || m.category || '-';

  // Honest page-local-sort flag (parity note): the full set is backend-ordered by
  // annual spend; a column sort reorders only the current page.
  const SORT_LABELS = {
    material_number: 'Material #', material_name: 'Name', level_2: 'Category',
    supplier_count: 'Suppliers', primary_supplier_name: 'Primary supplier', annual_spend: 'spend'
  };
  const sortIndicator = (sortColumn === 'annual_spend' && sortDirection === 'desc')
    ? 'Sorted by spend'
    : `Sorted by ${SORT_LABELS[sortColumn] || 'spend'} · this page`;

  const openMaterial = (row) => navigate(`/materials/${row.material_number}`);

  const sortHeader = (key, label, { right = false, hideSm = false } = {}) => {
    const cls = ['ll-sort'];
    if (right) cls.push('ll-hr');
    if (hideSm) cls.push('ll-hide-sm');
    if (sortColumn === key) cls.push(sortDirection === 'asc' ? 'll-asc' : 'll-desc');
    return (
      <span
        className={cls.join(' ')}
        role="button"
        tabIndex={0}
        onClick={() => handleSort(key)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort(key); } }}
      >{label}</span>
    );
  };

  // Taxonomy option renderer: values may be plain strings or {value, count} objects.
  const optionEls = (opts) => opts.map((v) => {
    const value = (v && v.value != null) ? v.value : v;
    const count = (v && v.count != null) ? v.count : null;
    const label = (v && v.label) || value;  // Lane 1385: shared category name; value stays raw
    return <option key={value} value={value}>{count != null ? `${label} (${count.toLocaleString()})` : label}</option>;
  });

  if (loading && materials.length === 0 && !error) {
    return (
      <PageShell className="ll">
        <div className="ll-wrap">
          <div className="ll-loading">Loading materials…</div>
        </div>
      </PageShell>
    );
  }

  if (error && materials.length === 0) {
    return (
      <PageShell className="ll">
        <div className="ll-wrap">
          <header className="ll-head"><div><h1 className="ll-h1">Materials</h1></div></header>
          <div className="ll-empty">
            <div className="ll-empty-t">Unable to load materials</div>
            {error} <button className="ll-clr" onClick={fetchMaterials} style={{ color: 'var(--ll-accent)' }}>Retry</button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell className="ll">
      <div className="ll-wrap">
        <header className="ll-head">
          <div>
            <h1 className="ll-h1">Materials</h1>
            <div className="ll-sub">Every material you buy. Find a part, or browse a category, on the way to its dossier</div>
          </div>
        </header>

        {/* catalog figure strip — the active-catalog count + how many of them were
            actually bought in the trailing 12 months (purchased_last_12mo_count is a
            COUNT over the same filtered set, on the SAME 12-mo window the annual_spend
            column uses, so it equals the number of rows below showing a $ figure).
            Spend-12mo / Single-source still need their own aggregates. */}
        <section className="ll-figs ll-figs--compact" aria-label="Catalog summary">
          <div className="ll-fig">
            <div className="ll-fig-k">Materials</div>
            <div className="ll-fig-v">{formatCount(totalCount)}</div>
            <div className="ll-fig-d">{hasActiveFilters ? 'match your filters · active' : 'active, in your catalog'}</div>
          </div>
          <div className="ll-fig">
            <div className="ll-fig-k">Purchased · 12 mo</div>
            <div className="ll-fig-v">{formatCount(purchasedCount)}</div>
            <div className="ll-fig-d">{hasActiveFilters ? 'of your matches · last 12 mo' : 'bought in the last 12 months'}</div>
          </div>
        </section>

        {/* filter bar: search + 4 cascading taxonomy levels (each option carries its count) */}
        <div className="ll-fbar">
          <div className="ll-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
            <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search material # or name" aria-label="Search materials" />
          </div>
          <select className="ll-sel" value={level1Filter} onChange={(e) => setLevel1Filter(e.target.value)} aria-label="Category (level 1)">
            <option value="">Category: all</option>
            {optionEls(level1Options)}
          </select>
          <select className="ll-sel" value={level2Filter} onChange={(e) => setLevel2Filter(e.target.value)} disabled={!level1Filter} aria-label="Subcategory (level 2)">
            <option value="">{level1Filter ? 'Subcategory: all' : 'Subcategory: pick a category'}</option>
            {optionEls(level2Options)}
          </select>
          <select className="ll-sel" value={level3Filter} onChange={(e) => setLevel3Filter(e.target.value)} disabled={!level2Filter} aria-label="Type (level 3)">
            <option value="">{level2Filter ? 'Type: all' : 'Type: pick a subcategory'}</option>
            {optionEls(level3Options)}
          </select>
          <select className="ll-sel" value={level4Filter} onChange={(e) => setLevel4Filter(e.target.value)} disabled={!level3Filter} aria-label="Detail (level 4)">
            <option value="">{level3Filter ? 'Detail: all' : 'Detail: pick a type'}</option>
            {optionEls(level4Options)}
          </select>
          {hasActiveFilters && <button className="ll-clr" onClick={clearFilters}>Clear</button>}
          <span className="ll-rt" title="Column sort reorders the current page; the full catalog is ordered by spend.">{sortIndicator}</span>
        </div>

        {loading && materials.length > 0 && <div className="ll-updating">Updating results…</div>}

        <div ref={materialsTableRef}>
          <TableShell className="ll-tbl ll-tbl--mat" aria-label="Materials">
            {!loading && displayMaterials.length === 0 ? (
              <div className="ll-empty">
                <div className="ll-empty-t">No materials found</div>
                {hasActiveFilters ? 'Try adjusting your search or filters' : 'No materials in your catalog yet'}
              </div>
            ) : (
              <>
                <div className="ll-row ll-hd">
                  {sortHeader('material_number', 'Material #')}
                  {sortHeader('material_name', 'Name')}
                  {sortHeader('level_2', 'Category', { hideSm: true })}
                  {sortHeader('supplier_count', 'Suppliers', { right: true, hideSm: true })}
                  {sortHeader('primary_supplier_name', 'Primary supplier', { hideSm: true })}
                  {sortHeader('annual_spend', 'Annual spend', { right: true })}
                </div>
                {displayMaterials.map((m) => (
                  <div
                    key={m.material_number}
                    className="ll-row"
                    tabIndex={0}
                    onClick={() => openMaterial(m)}
                    onKeyDown={(e) => { if (e.key === 'Enter') openMaterial(m); }}
                  >
                    <div className="ll-mno">{m.material_number}</div>
                    <div className="ll-mname" title={m.material_name}>{m.material_name}</div>
                    <div className="ll-cat ll-hide-sm" title={categoryOf(m)}>{categoryOf(m)}</div>
                    <div className="ll-num ll-mut ll-hide-sm">{m.supplier_count || 0}</div>
                    <div className="ll-sup ll-hide-sm" title={m.primary_supplier_name || ''}>{m.primary_supplier_name || '-'}</div>
                    {m.annual_spend > 0
                      ? <div className="ll-num ll-mono"><Currency value={m.annual_spend}/></div>
                      : <div className="ll-num ll-mut">None</div>}
                  </div>
                ))}
              </>
            )}
          </TableShell>
        </div>

        <LinearPager
          totalItems={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          scrollTargetRef={materialsTableRef}
          noun="materials"
        />

        <div className="ll-foot">
          <b>Annual spend</b> is strictly the trailing 12 months of PO value, in millions; “None” when nothing was purchased in the last 12 months. Only <b>active</b> materials appear. Single-source exposure (1 approved supplier) is read in full on the Material Dossier.
        </div>
      </div>
    </PageShell>
  );
}

export default MaterialList;
